<template>
  <div v-if="progress.items.length > 0" class="upload-progress-stack">
    <transition-group name="upload-progress-fade">
      <base-card
        v-for="item in progress.items"
        :key="item.id"
        class="upload-progress border rounded-lg bg-card"
        elevation="6"
      >
        <div class="d-flex align-center ga-3 px-4 py-3">
          <base-icon
            :icon="ICONS.uploadProgress.upload"
            size="20"
            color="primary"
          />

          <div class="upload-progress__body">
            <truncated-text
              class="text-body-2"
              :text="$t('labels.uploadingFile', { name: item.fileName })"
            />
            <div class="text-caption text-medium-emphasis mt-1">
              {{ statusLine(item) }}
            </div>
          </div>

          <base-button
            icon
            variant="text"
            size="small"
            :disabled="item.cancelling"
            :aria-label="$t('labels.cancel')"
            @click="cancelUpload(item.id)"
          >
            <base-icon size="16">{{ ICONS.uploadProgress.close }}</base-icon>
          </base-button>
        </div>

        <base-progress-linear
          :model-value="uploadFraction(item) * 100"
          color="primary"
          height="3"
          rounded
        />
      </base-card>
    </transition-group>
  </div>
</template>

<script lang="ts" setup>
  import { useI18n } from 'vue-i18n';
  import {
    BaseCard,
    BaseButton,
    BaseIcon,
    BaseProgressLinear
  } from '@deltax/vuetify-wrapper-components';
  import { ICONS } from '@/constants/icons';
  import {
    useUploadProgressStore,
    uploadFraction,
    uploadSecondsRemaining
  } from '@/stores/uploadProgress';
  import { cancelUpload } from '@/services/media/uploadSession';
  import type { UploadProgressItem } from '@/types/shared/uploadProgress';

  /**
   * Below this speed the connection is slow enough that the user deserves to
   * be told why a familiar-sized file is taking so long. 250 KB/s is roughly
   * the top of what a 3G uplink manages.
   */
  const SLOW_BYTES_PER_SECOND = 250 * 1024;

  const { t } = useI18n();
  const progress = useUploadProgressStore();

  /** Rounds to the largest unit that still reads sensibly, never "0 min". */
  function formatDuration(seconds: number): string {
    if (seconds >= 3600) {
      return t('labels.hoursShort', { count: Math.round(seconds / 3600) });
    }
    if (seconds >= 60) {
      return t('labels.minutesShort', { count: Math.round(seconds / 60) });
    }
    return t('labels.secondsShort', {
      count: Math.max(1, Math.round(seconds))
    });
  }

  /**
   * The line under the file name. It answers, in order of what the user most
   * needs to know: am I stopping, which part is going out, how long is left,
   * and whether the connection is the reason for the wait.
   */
  function statusLine(item: UploadProgressItem): string {
    if (item.cancelling) return t('labels.uploadCancelling');

    const part =
      item.chunkCount > 1
        ? t('labels.uploadPart', {
            index: item.chunkIndex,
            count: item.chunkCount
          })
        : '';

    const seconds = uploadSecondsRemaining(item);
    if (seconds === null) {
      return [part, t('labels.uploadEstimating')].filter(Boolean).join(' · ');
    }

    const time = t('labels.uploadTimeLeft', { time: formatDuration(seconds) });
    const slow =
      item.bytesPerSecond !== null &&
      item.bytesPerSecond < SLOW_BYTES_PER_SECOND
        ? t('labels.uploadSlowConnection')
        : '';

    return [part, time, slow].filter(Boolean).join(' · ');
  }
</script>

<style scoped>
  .upload-progress-stack {
    position: fixed;
    right: 16px;
    bottom: 16px;
    z-index: 2000;
    display: flex;
    flex-direction: column;
    gap: 8px;
  }

  .upload-progress {
    width: 340px;
    overflow: hidden;
  }

  .upload-progress__body {
    flex: 1 1 auto;
    min-width: 0;
  }

  .upload-progress-fade-enter-active,
  .upload-progress-fade-leave-active {
    transition:
      opacity 0.2s ease,
      transform 0.2s ease;
  }

  .upload-progress-fade-enter-from,
  .upload-progress-fade-leave-to {
    opacity: 0;
    transform: translateY(8px);
  }
</style>
